import { NewsletterForm } from '@/components/NewsletterForm';
import { PrimaryCTA } from '@/components/PrimaryCTA';
import Header from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ComebackBanner } from '@/components/ComebackBanner';
import { TrustBadges } from '@/components/TrustBadges';
import { TypewriterText } from '@/components/TypewriterText';
import { Search, Clock, Mail, Settings, LogOut, CheckCircle, Sparkles, ShieldCheck } from 'lucide-react';
import { Link } from "react-router-dom";
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { NewsletterSuccessModal } from '@/components/NewsletterSuccessModal';
import { SocialProof } from '@/components/SocialProof';
import { InteractiveNewsletterPreview } from '@/components/InteractiveNewsletterPreview';
import { TangibleBenefits } from '@/components/TangibleBenefits';
import { HowItWorksSimplified } from '@/components/HowItWorksSimplified';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { WhatsIncludedSection } from '@/components/WhatsIncludedSection';
import { StickySubscribeButton } from '@/components/StickySubscribeButton';
import { ProblemSolutionSection } from '@/components/ProblemSolutionSection';
import { useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import i18n from '../i18n';
import { AnimatedParticles } from '@/components/AnimatedParticles';
import { RotatingBadges } from '@/components/RotatingBadges';
import { useIsMobile } from "@/hooks/use-mobile";
import { useRandomSubheadline } from '@/hooks/useRandomSubheadline';
import { useTrialDays } from '@/hooks/useTrialDays';
import { useToast } from '@/hooks/use-toast';
import { useInitTracking } from '@/hooks/useInitTracking';
import { BannerProvider } from '@/contexts/BannerContext';
import { ExplainerVideo } from '@/components/ExplainerVideo';
import { DecommissioningModal } from '@/components/DecommissioningModal';
import { trackGTMEvent } from '@/components/GoogleTagManager';
import { trackEventMp } from '@/components/Mixpanel';

const Index = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { subheadlineText } = useRandomSubheadline();
  const { trialDays } = useTrialDays();
  const { toast } = useToast();
  
  // Tracking silencioso de visitantes
  useInitTracking();
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successEmail, setSuccessEmail] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);
  const [showDecommissioningModal, setShowDecommissioningModal] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkSession();
  }, []);

  const openDecommissioningModal = () => {
    setShowDecommissioningModal(true);
    trackGTMEvent('decommissioning_modal_viewed', { source: 'newsforyou_ai' });
    trackEventMp('decommissioning_modal_viewed', { source: 'newsforyou_ai' });
  };

  useEffect(() => {
    if (searchParams.get('service') === 'discontinued') {
      openDecommissioningModal();
      const url = new URL(window.location.href);
      url.searchParams.delete('service');
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, [searchParams]);

  useEffect(() => {
    // Detect and show session errors
    const error = searchParams.get('error');
    
    if (error === 'invalid_token') {
      toast({
        title: t('sessionErrors.invalidToken.title'),
        description: t('sessionErrors.invalidToken.description'),
        variant: "destructive",
      });
    } else if (error === 'session_expired') {
      toast({
        title: t('sessionErrors.expired.title'),
        description: t('sessionErrors.expired.description'),
        variant: "destructive",
      });
    }
    
    // Clear error from URL
    if (error) {
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
  }, [searchParams, toast]);

  useEffect(() => {
    // Check if user came from signup flow and scroll to form
    const shouldScrollToForm = searchParams.get('signup') === 'true';
    if (shouldScrollToForm && !isLoading) {
      setTimeout(() => {
        const formElement = document.getElementById('newsletter-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [searchParams, isLoading]);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY <= 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const checkSession = async () => {
    try {
      const sessionData = localStorage.getItem('newsletter_session');
      if (!sessionData) {
        setIsLoading(false);
        return;
      }

      const { session_token, expires_at, email } = JSON.parse(sessionData);
      
      if (!session_token || !expires_at) {
        localStorage.removeItem('newsletter_session');
        setIsLoading(false);
        return;
      }

      // Check if session is expired
      const now = new Date();
      const expiresAt = new Date(expires_at);
      
      if (expiresAt <= now) {
        localStorage.removeItem('newsletter_session');
        setIsLoading(false);
        return;
      }

      // Validate session with backend
      const { data, error } = await supabase.functions.invoke('validate-session', {
        body: { session_token }
      });

      if (error || !data.success) {
        localStorage.removeItem('newsletter_session');
        setIsLoading(false);
        return;
      }

      setIsLoggedIn(true);
      setUserEmail(data.user.email);
    } catch (error) {
      console.error('Error checking session:', error);
      localStorage.removeItem('newsletter_session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('newsletter_session');
    setIsLoggedIn(false);
    setUserEmail('');
  };

  const handleSubscriptionSuccess = (email: string) => {
    setSuccessEmail(email);
    setShowSuccessModal(true);
  };

  return (
    <BannerProvider>
      <ComebackBanner />
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Animated Particles Background */}
        <AnimatedParticles />
      
      {/* Background gradient */}
      <div className="absolute inset-0 bg-newsletter-gradient opacity-5" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-newsletter-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-newsletter-accent/10 rounded-full blur-3xl" />
      
      {/* Main content wrapper */}
      <div className="relative z-10 min-h-screen">
        {/* User Header - Only shown when logged in */}
        {isLoggedIn && !isLoading && (
          <div className="relative z-20 bg-background/95 backdrop-blur-md border-b border-newsletter-primary/10">
            <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-newsletter-gradient rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-muted-foreground">{t('userHeader.loggedAs')}</p>
                    <p className="font-medium text-sm sm:text-base">{userEmail}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                  <Link to="/preferences">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="hover:bg-newsletter-primary/10 hover:text-newsletter-primary transition-all duration-200 backdrop-blur-sm border border-transparent hover:border-newsletter-primary/20 px-2 sm:px-3"
                    >
                      <Settings className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">{t('userHeader.settings')}</span>
                    </Button>
                  </Link>
                  
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout} 
                    className="hover:bg-newsletter-primary/10 hover:text-newsletter-primary transition-all duration-200 backdrop-blur-sm border border-transparent hover:border-newsletter-primary/20 px-2 sm:px-3"
                  >
                    <LogOut className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('userHeader.logout')}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <Header />
        
        <div className="container mx-auto px-4 py-4">
          {/* Hero Section - New Layout */}
          <div className="mb-12 animate-fade-in">
            <div className="grid lg:grid-cols-[auto_auto] gap-8 lg:gap-12 items-start justify-center mx-auto px-4">
              {/* Left: Copy + Form */}
              <div className={`text-center lg:text-left ${i18n.language === 'pt-BR' ? 'lg:max-w-3xl' : 'lg:max-w-2xl'} lg:pr-4 lg:pt-16`}>
                <h1 className={`text-4xl md:text-5xl ${i18n.language === 'pt-BR' ? 'lg:text-5xl' : 'lg:text-6xl'} font-bold mb-6 leading-tight`}>
                  <span className="bg-gradient-to-r from-foreground via-newsletter-primary/80 to-newsletter-accent/60 bg-clip-text text-transparent">
                    {t('hero.headline').split('<1>')[0]}
                    {isMobile && i18n.language === 'pt-BR' && <br />}
                    <TypewriterText className="font-extrabold bg-gradient-to-r from-purple-500 via-fuchsia-500 via-pink-500 via-fuchsia-500 to-purple-500 bg-clip-text text-transparent animate-shimmer drop-shadow-[0_0_12px_rgba(168,85,247,0.35)]" />
                    <br />
                    {t('hero.headline').split('</1>')[1]?.replace('<br/>', '')}
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
                  {subheadlineText}
                </p>

                {/* Morning Routine - Rotating Badges */}
                <RotatingBadges />

                {/* Primary CTA */}
                <div className="mb-8">
                  <PrimaryCTA
                    onSuccess={handleSubscriptionSuccess}
                    onDecommissioningOpen={openDecommissioningModal}
                  />
                </div>
              </div>

              {/* Interactive Preview - Single conditional render */}
              {!isMobile && (
                <div className="lg:pl-4">
                  <InteractiveNewsletterPreview 
                    variant="rotating"
                    showCycleButton={true}
                    onSelectTemplate={(interests) => {
                      const textarea = document.querySelector('textarea[name="interests"]') as HTMLTextAreaElement;
                      if (textarea) {
                        textarea.value = interests;
                        textarea.dispatchEvent(new Event('input', { bubbles: true }));
                      }
                    }}
                  />
                </div>
              )}
            </div>

            {/* Mobile: Preview below hero */}
            {isMobile && (
              <div className="mt-12">
                <InteractiveNewsletterPreview 
                  variant="rotating"
                  showCycleButton={true}
                  onSelectTemplate={(interests) => {
                    const textarea = document.querySelector('textarea[name="interests"]') as HTMLTextAreaElement;
                    if (textarea) {
                      textarea.value = interests;
                      textarea.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Explainer Video Section */}
          <div className="mb-16 max-w-4xl mx-auto animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-newsletter-primary bg-clip-text text-transparent">
                {t('explainerVideo.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('explainerVideo.subtitle')}
              </p>
            </div>
            <ExplainerVideo />
          </div>

          {/* Social Proof */}
          <SocialProof />

          {/* Problem-Solution Section */}
          <ProblemSolutionSection />

          {/* Tangible Benefits */}
          <TangibleBenefits />

          {/* How It Works - Simplified */}
          <HowItWorksSimplified />

          {/* What's Included - NEW */}
          <WhatsIncludedSection />

          {/* Newsletter Form - Enhanced */}
          <div id="newsletter-form" className="mb-16 relative">
            <div className="absolute -inset-8 bg-newsletter-gradient-subtle opacity-30 rounded-3xl blur-2xl"></div>
            <div className="relative bg-gradient-to-br from-background/80 to-newsletter-primary/5 backdrop-blur-md border border-newsletter-primary/30 hover:border-newsletter-primary/50 rounded-3xl p-6 md:p-12 shadow-newsletter transition-all duration-500 group animate-fade-in max-w-4xl mx-auto">
              <div className="absolute -inset-0.5 bg-newsletter-gradient opacity-20 rounded-3xl blur-sm group-hover:opacity-30 transition-opacity duration-500"></div>
              <div className="absolute inset-4 bg-newsletter-primary/5 rounded-2xl blur-xl animate-pulse opacity-60"></div>
              <div className="absolute inset-6 bg-newsletter-primary/8 rounded-xl blur-lg animate-pulse opacity-40" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
              <div className="relative">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-foreground to-newsletter-primary bg-clip-text text-transparent">
                    {t('newsletterForm.title')}
                  </h3>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-newsletter-accent/10 border border-newsletter-accent/20 mb-4">
                    <Sparkles className="w-4 h-4 text-newsletter-accent" />
                    <span className="text-sm font-medium text-newsletter-accent">
                      {t('newsletterForm.badge')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('newsletterForm.benefits')}
                  </p>
                </div>
                <NewsletterForm
                  onSuccess={handleSubscriptionSuccess}
                  onDecommissioningOpen={openDecommissioningModal}
                />
              </div>
            </div>
          </div>

          {/* FAQ */}
          <FAQ />

          {/* Final CTA */}
          <FinalCTA onOpenDecommissioning={openDecommissioningModal} />

          {/* Features */}
          <div className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">{t('features.title')}</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center animate-fade-in-up">
              <div className="p-6 rounded-2xl bg-background/5 backdrop-blur-sm border border-newsletter-primary/10 hover:border-newsletter-primary/20 transition-all duration-300">
                <div className="w-12 h-12 bg-newsletter-gradient rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('features.curation.title')}</h3>
                <p className="text-muted-foreground text-sm">{t('features.curation.description')}</p>
              </div>
              
              <div className="p-6 rounded-2xl bg-background/5 backdrop-blur-sm border border-newsletter-primary/10 hover:border-newsletter-primary/20 transition-all duration-300">
                <div className="w-12 h-12 bg-newsletter-gradient rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('features.timesSaving.title')}</h3>
                <p className="text-muted-foreground text-sm">{t('features.timesSaving.description')}</p>
              </div>
              
              <div className="p-6 rounded-2xl bg-background/5 backdrop-blur-sm border border-newsletter-primary/10 hover:border-newsletter-primary/20 transition-all duration-300">
                <div className="w-12 h-12 bg-newsletter-gradient rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('features.email.title')}</h3>
                <p className="text-muted-foreground text-sm">{t('features.email.description')}</p>
              </div>
            </div>
          </div>

          {/* Preferences Link - Only shown when not logged in */}
          {!isLoggedIn && !isLoading && (
            <div className="text-center animate-fade-in-up">
              <div className="max-w-md mx-auto p-6 rounded-2xl bg-background/10 backdrop-blur-sm border border-newsletter-primary/20">
                <h3 className="text-lg font-semibold mb-3">{t('existingSubscriber.title')}</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {t('existingSubscriber.description')}
                </p>
                <Link to="/preferences/request">
                  <Button variant="outline" className="w-full border-newsletter-primary/30 hover:border-newsletter-primary hover:bg-newsletter-primary/10">
                    {t('existingSubscriber.button')}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>

        <TrustBadges />
        <Footer />
      </div>

      {/* Success Modal */}
      <NewsletterSuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        userEmail={successEmail}
      />

      {/* Sticky Subscribe Button */}
      <StickySubscribeButton onSubscribe={() => {
        const formElement = document.getElementById('newsletter-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }} />
      <DecommissioningModal
        open={showDecommissioningModal}
        onOpenChange={setShowDecommissioningModal}
      />
      </div>
    </BannerProvider>
  );
};

export default Index;
